import React from 'react';
import { useAuthenticate } from './useAuthenticate';
import { hasAssignedLocations } from '../utils/funcs';
import { IFilter } from '../models/index';
import { genericMerchantDB, remoteDB } from '../integrations/pouch/DBConn';
import { getSessionInfo } from '../utils/getSessionInfo';
import { handleCouchError } from '../utils/handleCouchError';
import { Tracking } from '../analytics/tracking';
import { EVENTS } from '../utils/events/index';
import { debounce } from 'lodash';
import { captureException } from '../integrations/sentry/capture';

interface IReplicator {
	localDBName: string;
	filterInitial: IFilter;
	filterSubsequent: IFilter;
	filterFunc?: (v: { [key: string]: unknown }) => { [key: string]: unknown };
	handleReplicationStatus: (v: boolean) => void;
	updateOnChange: (arg: Record<string, string>) => Promise<void>;
}

const getDebouncedTrackChange = (dbName: string) =>
	debounce((docsLength: number) => {
		Tracking.capture(EVENTS.REPLICATION.CHANGE(dbName), {
			direction: 'pull',
			docCount: docsLength,
			timestamp: Date.now(),
		});
	}, 10000);

export const useReplicatorFrom = ({
	localDBName,
	filterInitial,
	filterSubsequent,
	filterFunc,
	handleReplicationStatus,
	updateOnChange,
}: IReplicator) => {
	const [isReplicating, setIsReplicating] = React.useState(false);

	const replicateRef = React.useRef<any>(null);
	const liveReplicationRef = React.useRef<any>(null);
	const isAuthenticated = useAuthenticate();
	const { userInfo } = getSessionInfo();

	const debouncedTrackChange = getDebouncedTrackChange(localDBName);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => {
		if (!isAuthenticated) return;

		if (!hasAssignedLocations(userInfo)) return;

		handleReplicationStatus(true);
		setIsReplicating(true);

		Tracking.capture(EVENTS.REPLICATION.STARTED(localDBName), {
			userId: userInfo?._id,
			dbName: localDBName,
			direction: 'from',
			startedAt: new Date().toISOString(),
		});

		replicateRef.current = genericMerchantDB(localDBName)
			.replicate.from(remoteDB(userInfo?.merchantName as string), {
				...(filterFunc
					? filterFunc({ ...filterInitial, live: undefined, retry: true })
					: { ...filterInitial, live: undefined, retry: true }),
				timeout: 3000,
			})
			.on('complete', (info) => {
				if (info.status === 'complete') {
					Tracking.capture(EVENTS.REPLICATION.completed(localDBName), {
						userId: userInfo?._id,
						dbName: localDBName,
						direction: 'from',
						status: 'complete',
						replicatedAt: new Date().toISOString(),
						type: 'one-time',
					});

					handleReplicationStatus(false);
					setIsReplicating(false);

					liveReplicationRef.current = genericMerchantDB(localDBName)
						.replicate.from(
							remoteDB(userInfo?.merchantName as string),
							filterFunc
								? filterFunc({ ...filterSubsequent, live: true, retry: true })
								: { ...filterSubsequent, live: true, retry: true }
						)
						.on('error', (err: any) => {
							Tracking.capture(EVENTS.REPLICATION.FAILED(localDBName), {
								userId: userInfo?._id,
								dbName: localDBName,
								direction: 'from',
								status: 'error',
								error: err,
								type: 'live',
							});
							handleCouchError(err);
							captureException(err, { fn: 'useReplicatorFrom.liveReplication.error', args: { localDBName } });
						})
						.on('change', async (info: PouchDB.Replication.ReplicationResult<{}>) => {
							debouncedTrackChange(info.docs?.length ?? 0);
							await updateOnChange(info?.docs?.map((doc) => doc) as any);
						});
				}
			})
			.on('error', (error) => {
				Tracking.capture(EVENTS.REPLICATION.FAILED(localDBName), {
					userId: userInfo?._id,
					dbName: localDBName,
					direction: 'from',
					status: 'error',
					error: error,
					type: 'one-time',
				});
				handleCouchError(error);
				captureException(error, { fn: 'useReplicatorFrom.oneTimeReplication.error', args: { localDBName } });
			})
			.on('change', async (info: PouchDB.Replication.ReplicationResult<{}>) => {
				debouncedTrackChange(info.docs?.length ?? 0);
				await updateOnChange(info.docs?.map((doc) => doc) as any);
			})
			.on('denied', (info: any) => {
				captureException(new Error('replication_denied'), {
					fn: 'useReplicatorFrom.replication.denied',
					args: { localDBName, info },
				});
			});

		return () => {
			replicateRef.current?.cancel();
			liveReplicationRef.current?.cancel();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { isReplicating };
};
